import { useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import analytics from "../analytics";

/**
 * Custom hook for analytics tracking throughout the app
 * Similar to what AutoCheck.com would use for comprehensive tracking
 */
export const useAnalytics = () => {
  const location = useLocation();

  // NOTE: Analytics initialization now handled in App.js AFTER bot check
  // This matches legacy Vue.js behavior where loadAnalytics() is only called
  // after callToLoadAnalytics() confirms the user is not a bot
  useEffect(() => {
     // No auto-initialization - wait for App.js to initialize after bot check
    console.debug('📊 useAnalytics hook ready (initialization handled by App.js)');

  }, []);

  // Track page views on route changes
  useEffect(() => {
    analytics.trackPageView(location.pathname + location.search);
  }, [location]);

  // Memoized tracking functions
  const trackEvent = useCallback((eventName, parameters) => {
    analytics.trackEvent(eventName, parameters);
  }, []);

  const trackVinSearch = useCallback((vin, searchType) => {
    analytics.trackVinSearch(vin, searchType);
  }, []);

  const trackReportView = useCallback((reportData) => {
    analytics.trackReportView(reportData);
  }, []);

  const trackPurchase = useCallback((transactionData) => {
    analytics.trackPurchase(transactionData);
  }, []);

  const trackFormSubmission = useCallback((formName, formData) => {
    analytics.trackFormSubmission(formName, formData);
  }, []);

  const trackFormSuccessfulSubmit = useCallback((formName, formData) => {
    analytics.trackFormSuccessfulSubmit(formName, formData);
  }, []);

  const trackDownload = useCallback((fileName, fileType) => {
    analytics.trackDownload(fileName, fileType);
  }, []);

  const trackExternalLink = useCallback((url, linkText) => {
    analytics.trackExternalLink(url, linkText);
  }, []);

  const setUserId = useCallback((userId) => {
    analytics.setUserId(userId);
  }, []);

  const setUserProperties = useCallback((properties) => {
    analytics.setUserProperties(properties);
  }, []);

  // AutoCheck specific tracking functions
  const trackAutocheckScoreInteraction = useCallback((action, scoreData) => {
    analytics.trackAutocheckScoreInteraction(action, scoreData);
  }, []);

  const trackSampleReportView = useCallback((sampleType, reportData) => {
    analytics.trackSampleReportView(sampleType, reportData);
  }, []);

  const trackPartnerReferral = useCallback((partnerData) => {
    analytics.trackPartnerReferral(partnerData);
  }, []);

  const trackReportChoiceSelection = useCallback((choiceData) => {
    analytics.trackReportChoiceSelection(choiceData);
  }, []);

  const trackMobileAppInteraction = useCallback((action, appData) => {
    analytics.trackMobileAppInteraction(action, appData);
  }, []);

  return {
    // Core tracking functions
    trackEvent,
    trackPageView: analytics.trackPageView.bind(analytics),

    // AutoCheck specific tracking
    trackVinSearch,
    trackReportView,
    trackPurchase,

    // Interaction tracking
    trackFormSubmission,
    trackFormSuccessfulSubmit,
    trackDownload,
    trackExternalLink,

    // AutoCheck business-specific tracking
    trackAutocheckScoreInteraction,
    trackSampleReportView,
    trackPartnerReferral,
    trackReportChoiceSelection,
    trackMobileAppInteraction,

    // User management
    setUserId,
    setUserProperties,

    // Current page info
    currentPath: location.pathname,
    currentSearch: location.search,
    currentUrl: location.pathname + location.search,
  };
};

/**
 * Hook for tracking form interactions
 */
export const useFormTracking = (formName) => {
  const { trackFormSubmission, trackFormSuccessfulSubmit, trackEvent } =
    useAnalytics();

  const trackFormStart = useCallback(() => {
    trackEvent("form_start", { form_name: formName });
  }, [trackEvent, formName]);

  const trackFormError = useCallback(
    (errorField, errorMessage) => {
      trackEvent("form_error", {
        form_name: formName,
        error_field: errorField,
        error_message: errorMessage,
      });
    },
    [trackEvent, formName]
  );

  const trackFormSubmit = useCallback(
    (formData = {}) => {
      trackFormSubmission(formName, formData);
    },
    [trackFormSubmission, formName]
  );

  const trackFormSuccessSubmit = useCallback(
    (formData = {}) => {
      trackFormSuccessfulSubmit(formName, formData);
    },
    [trackFormSuccessfulSubmit, formName]
  );

  const trackFieldFocus = useCallback(
    (fieldName) => {
      trackEvent("form_field_focus", {
        form_name: formName,
        field_name: fieldName,
      });
    },
    [trackEvent, formName]
  );

  return {
    trackFormStart,
    trackFormError,
    trackFormSubmit,
    trackFormSuccessSubmit,
    trackFieldFocus,
  };
};

/**
 * Hook for tracking ecommerce events (AutoCheck reports)
 */
export const useEcommerceTracking = () => {
  const { trackEvent, trackPurchase, trackReportView } = useAnalytics();

  const trackAddToCart = useCallback(
    (item) => {
      trackEvent("add_to_cart", {
        item_id: item.vin || item.id,
        item_name: item.name || `Vehicle Report - ${item.vin}`,
        item_category: "vehicle_report",
        price: parseFloat(item.price),
        currency: "USD",
        quantity: 1,
      });
    },
    [trackEvent]
  );

  const trackRemoveFromCart = useCallback(
    (item) => {
      trackEvent("remove_from_cart", {
        item_id: item.vin || item.id,
        item_name: item.name || `Vehicle Report - ${item.vin}`,
        price: parseFloat(item.price),
        currency: "USD",
      });
    },
    [trackEvent]
  );

  const trackBeginCheckout = useCallback(
    (items, value) => {
      trackEvent("begin_checkout", {
        currency: "USD",
        value: parseFloat(value),
        items: items,
      });
    },
    [trackEvent]
  );

  const trackPurchaseComplete = useCallback(
    (transactionData) => {
      trackPurchase(transactionData);
    },
    [trackPurchase]
  );

  const trackViewReport = useCallback(
    (reportData) => {
      trackReportView(reportData);
    },
    [trackReportView]
  );

  return {
    trackAddToCart,
    trackRemoveFromCart,
    trackBeginCheckout,
    trackPurchaseComplete,
    trackViewReport,
  };
};

export default useAnalytics;
